import { Button, Icon, HorizontalDivider, Input, Typography } from '@smartgift/frontend-toolkit';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MAX_DOMAIN_LENGTH } from '@hero/constants';
import { isValidDomain } from '@hero/utils/validationUtils';

import * as S from './style';

function DomainManagement({ emailDomains = [], setEmailDomains, onSave }) {
  const { t } = useTranslation();
  const [isReadOnly, setIsReadOnly] = useState(emailDomains.length > 0 && emailDomains[0] !== '');

  function addNewDomain(emailDomain) {
    return setEmailDomains([...emailDomains, emailDomain]);
  }

  const domainsIsValid = useMemo(() => {
    return (
      emailDomains.filter(emailDomain => !isValidDomain(emailDomain) && emailDomain !== '')
        .length === 0
    );
  }, [emailDomains]);

  useEffect(() => {
    if (emailDomains.length === 0) {
      addNewDomain('');
    }
  }, []);

  return (
    <S.DomainManagementWrapper>
      {isReadOnly ? (
        <>
          <S.DomainSubTitle>
            <Typography variant="body3Bold">
              {t('domains')}:{' '}
              <Typography variant="body2Bold" style={{ display: 'inline' }}>
                {emailDomains.length}
              </Typography>
            </Typography>
            <Button
              variant="link"
              onClick={() => {
                setIsReadOnly(false);
              }}
              startIcon={<Icon name="edit" size="md" />}
            >
              {t('editDomains')}
            </Button>
          </S.DomainSubTitle>
          <S.ReadOnlyDomainList>
            {emailDomains.map(emailDomain => {
              return (
                <Typography variant="body3" key={emailDomain}>
                  {emailDomain}
                </Typography>
              );
            })}
          </S.ReadOnlyDomainList>
        </>
      ) : (
        <>
          {emailDomains.map((emailDomain, index) => {
            const isValid = emailDomains[index] === '' || isValidDomain(emailDomains[index]);
            return (
              <S.EmailDomainArea key={index}>
                <Input
                  variant="outlined"
                  value={emailDomain}
                  label={t('domainName')}
                  error={!isValid}
                  helperText={!isValid && t('domainIsNotValid')}
                  inputProps={{ maxLength: MAX_DOMAIN_LENGTH }}
                  onChange={e => {
                    emailDomains[index] = e.target.value;
                    setEmailDomains([...emailDomains]);
                  }}
                  style={{ width: 360 }}
                />
                {emailDomains.length > 1 && (
                  <Button
                    variant="text"
                    startIcon={<Icon name="delete" color="var(--color-error-stronger)" />}
                    disabled={!emailDomains[index] && emailDomains.length === 1}
                    onClick={() => {
                      if (emailDomains.length <= 1) {
                        setEmailDomains(['']);
                      } else {
                        setEmailDomains(emailDomains.filter((_e, i) => index !== i));
                      }
                    }}
                  >
                    <Typography color="error" variant="body2Bold">
                      {t('remove')}
                    </Typography>
                  </Button>
                )}
              </S.EmailDomainArea>
            );
          })}

          <Button variant="text" startIcon={<Icon name="add" />} onClick={() => addNewDomain('')}>
            {t('addADomain')}
          </Button>

          <HorizontalDivider $margin="8px 0 16px 0" />
          <S.ButtonGroup>
            <Button
              color="secondary"
              variant="outlined"
              onClick={() => {
                setEmailDomains(['']);
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="outlined"
              disabled={!domainsIsValid || (emailDomains.length === 1 && emailDomains[0] === '')}
              onClick={() => {
                setIsReadOnly(true);
                if (onSave) onSave();
              }}
            >
              {t('save')}
            </Button>
          </S.ButtonGroup>
        </>
      )}
    </S.DomainManagementWrapper>
  );
}

export { DomainManagement };
